import { useCallback } from 'react';
import analytics from 'storefront-analytics/dist/analytics';

import {
  EventEmitter,
  TrackClickFunc,
  TrackEventFunc,
  TrackImpressionFunc,
  TrackPageViewFunc,
} from '@src/@types/analytics';
import { useDynatrace } from '@src/hooks/use-dynatrace.hook';

/**
 * Builds purpose-oriented analytics emission functions based on the provided
 * initialization-safe {@link emitEvent}. Put all business logic wrappers for
 * analytics event enrichment in this hook.
 */
export const useAnalyticsEvents = (
  emitEvent: (eventEmitter: EventEmitter) => void,
) => {
  const { enterAction, leaveAction } = useDynatrace();
  const trackPageView: TrackPageViewFunc = useCallback(
    (extraData?: Record<string, any>) => {
      const actionId = enterAction('Page View');

      emitEvent(() =>
        analytics.trackPageView(undefined, extraData ? extraData : {}),
      );

      leaveAction(actionId);
    },
    [emitEvent],
  );

  const trackClick: TrackClickFunc = useCallback(
    (args) => {
      const {
        eventLabel,
        eventValue,
        extraData = {},
        eventCategory = 'petInsurance',
      } = args;

      const actionId = enterAction(`Click: ${eventLabel}`);

      emitEvent(() =>
        analytics.trackEvent(
          eventCategory,
          'click',
          eventLabel,
          eventValue,
          extraData,
        ),
      );

      leaveAction(actionId);
    },
    [emitEvent],
  );

  const trackEvent: TrackEventFunc = useCallback(
    (args) => {
      const {
        eventAction,
        eventCategory,
        extraData = {},
        eventLabel,
        eventValue = undefined,
      } = args;

      const actionId = enterAction(`Event: ${eventLabel} - ${eventAction}`);

      emitEvent(() =>
        analytics.trackEvent(
          eventCategory,
          eventAction,
          eventLabel,
          eventValue,
          extraData,
        ),
      );

      leaveAction(actionId);
    },
    [emitEvent],
  );

  const trackImpression: TrackImpressionFunc = useCallback(
    (args) => {
      const {
        eventLabel,
        eventValue,
        extraData = {},
        eventCategory = 'petInsurance',
      } = args;

      const actionId = enterAction(`Impression: ${eventLabel}`);

      emitEvent(() =>
        analytics.trackEvent(
          eventCategory,
          'impression',
          eventLabel,
          eventValue,
          extraData,
        ),
      );

      leaveAction(actionId);
    },
    [emitEvent],
  );

  return {
    trackPageView,
    trackClick,
    trackEvent,
    trackImpression,
  };
};
